import { useErrorHandlerContext, usePropertyDetailContext, useUserLanguage } from '@/customHooks';
import { useInventoryInspections, useGenerateInventoryInspectionPdf } from '@/inventoryApi';
import { downloadFileFromUrl } from '@/helpers';
import { isFinalInventoryType } from '@/helpers/inventoryCatalog';
import type { InventoryInspectionOutput } from '@/types/custom-types';
import textsData from '@/textData/PropertyDetailTexts.json';
import { Button } from '../atoms';
import { ButtonWithDropDownMenu } from '../molecules';
import { LoadingSpinner } from '../atoms';

type PropertyDetailInventoriesListProps = {
  onCreate: (propertyId: string) => void;
  onEdit: (propertyId: string, inventory: InventoryInspectionOutput) => void;
  onDetails: (propertyId: string, inventory: InventoryInspectionOutput) => void;
};

export const PropertyDetailInventoriesList = ({
  onCreate,
  onEdit,
  onDetails,
}: PropertyDetailInventoriesListProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.inventoriesList : textsData.en.inventoriesList;

  const { property } = usePropertyDetailContext();
  const { open: errorHandle } = useErrorHandlerContext();

  const { data: inventories, loading, error, refetch } = useInventoryInspections(property?.id, {
    onError: errorHandle,
  });
  const { generate: generatePdf } = useGenerateInventoryInspectionPdf({
    onError: errorHandle,
  });

  const handleNewInventory = () => {
    if (!property?.id) return;
    onCreate(property.id);
  };

  const totalInventories = inventories?.length ?? 0;
  const initialInventories =
    inventories?.filter((inv) => !isFinalInventoryType(inv.type)).length ?? 0;
  const finalInventories =
    inventories?.filter((inv) => isFinalInventoryType(inv.type)).length ?? 0;

  const handleEditInventory = (inventory: InventoryInspectionOutput) => {
    if (!property?.id) return;
    onEdit(property.id, inventory);
  };

  const handleDetailsInventory = (inventory: InventoryInspectionOutput) => {
    if (!property?.id) return;
    onDetails(property.id, inventory);
  };

  const handleDownloadPdf = async (inventory: InventoryInspectionOutput) => {
    if (!property?.id) return;

    try {
      const payload = await generatePdf(property.id, inventory.id);
      if (!payload?.url) return;

      await downloadFileFromUrl(payload.url, payload.fileName ?? 'inspection.pdf');
    } catch (caught) {
      errorHandle(caught);
    }
  };

  if (!property?.id) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-white p-6 text-center shadow-md">
        <p className="font-semibold text-red-700">
          {userLanguage === 'es'
            ? 'No se pudo cargar el inventario'
            : 'Could not load inventory'}
        </p>
        <p className="mt-2 text-sm text-slate-700">{error.message}</p>
        <div className="mt-4">
          <Button onClick={() => void refetch()}>
            {userLanguage === 'es' ? 'Reintentar' : 'Retry'}
          </Button>
        </div>
      </div>
    );
  }

  const typeBadge = (inventory: InventoryInspectionOutput) => (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-semibold text-white ${
        isFinalInventoryType(inventory.type) ? 'bg-primary1' : 'bg-primary2'
      }`}
    >
      {isFinalInventoryType(inventory.type)
        ? texts.typeLabels.final
        : texts.typeLabels.initial}
    </span>
  );

  const draftBadge = (inventory: InventoryInspectionOutput) =>
    inventory.isDraft ? (
      <span className="inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
        {texts.pendingBadge}
      </span>
    ) : null;

  const menu = (inventory: InventoryInspectionOutput) => (
    <ButtonWithDropDownMenu
      options={[
        { item: texts.dropdownMenu.details, action: () => handleDetailsInventory(inventory) },
        { item: texts.dropdownMenu.edit, action: () => handleEditInventory(inventory) },
        { item: texts.dropdownMenu.downloadPdf, action: () => handleDownloadPdf(inventory) },
      ]}
      position="left"
    />
  );

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{texts.title}</h2>
        </div>
        <div>
          <Button onClick={handleNewInventory}>+ {texts.createButton}</Button>
        </div>
      </div>
      <div className="mt-4 mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{totalInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.saved}</span>
        </div>
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{initialInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.initial}</span>
        </div>
        <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-md">
          <span className="mt-1 text-3xl font-bold">{finalInventories}</span>
          <span className="mt-2 text-sm text-slate-700">{texts.stats.final}</span>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white p-6 shadow-md">
        {!inventories || inventories.length === 0 ? (
          <div className="flex min-h-[180px] flex-col items-center justify-center text-center text-slate-700">
            <p className="font-semibold">{texts.noData}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <div className="grid grid-cols-[1.5fr_1fr_0.8fr_100px] border-b border-gray-200 py-3 text-sm font-semibold">
                <span>{texts.tableHeader.description}</span>
                <span>{texts.tableHeader.itemsCount}</span>
                <span>{texts.tableHeader.type}</span>
                <span />
              </div>
              {inventories.map((inventory) => (
                <div
                  key={inventory.id}
                  className="grid grid-cols-[1.5fr_1fr_0.8fr_100px] items-center border-b border-gray-200 py-3 text-sm"
                >
                  <span>{inventory.generalNotes || texts.tableHeader.untitledInventory}</span>
                  <span>
                    {inventory.sections.reduce(
                      (acc: number, section) => acc + section.items.length,
                      0,
                    )}
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    {typeBadge(inventory)}
                    {draftBadge(inventory)}
                  </span>
                  <span>{menu(inventory)}</span>
                </div>
              ))}
            </div>

            {/* Mobile cards */}
            <div className="flex flex-col gap-3 md:hidden">
              {inventories.map((inventory) => {
                const itemCount = inventory.sections.reduce(
                  (acc: number, section) => acc + section.items.length,
                  0,
                );
                return (
                  <div
                    key={inventory.id}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-800">
                          {inventory.generalNotes || texts.tableHeader.untitledInventory}
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                          {texts.tableHeader.itemsCount}: {itemCount}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {typeBadge(inventory)}
                          {draftBadge(inventory)}
                        </div>
                      </div>
                      <div className="shrink-0">{menu(inventory)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};
